import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaClock, FaCheck, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios';
import API_BASE_URL from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import './BookingCalendar.css';

const BookingCalendar = ({ 
  availableDates, 
  selectedDate, 
  selectedTime,
  onDateSelect, 
  onTimeSelect,
  showTimeSlots = false,
  loading = false,
  minTime = null
}) => {
  const { t, language } = useLanguage();
  const [timeSlots, setTimeSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dateInputValue, setDateInputValue] = useState('');
  const [showDateInput, setShowDateInput] = useState(false);
  const [fullDateInputValue, setFullDateInputValue] = useState('');
  const [showFullDateInput, setShowFullDateInput] = useState(false);

  useEffect(() => {
    if (selectedDate && showTimeSlots) {
      fetchTimeSlots(selectedDate);
    }
  }, [selectedDate, showTimeSlots]);


  const fetchTimeSlots = async (date) => {
    try {
      setLoadingSlots(true);
      const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
      const response = await axios.get(`${API_BASE_URL}/api/bookings/availability/${dateStr}`);
      setTimeSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error fetching time slots:', error);
      setTimeSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handlePrevMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setCurrentMonth(newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    setCurrentMonth(newMonth);
  };


  const handleDateInputChange = (e) => {
    setDateInputValue(e.target.value);
  };

  const handleDateInputSubmit = (e) => {
    e.preventDefault();
    if (dateInputValue) {
      const inputDate = new Date(dateInputValue);
      if (!isNaN(inputDate.getTime())) {
        setCurrentMonth(inputDate);
        setShowDateInput(false);
        setDateInputValue('');
      }
    }
  };

  const handleMonthTitleClick = () => {
    setShowDateInput(!showDateInput);
    if (!showDateInput) {
      // Устанавливаем текущую дату в формате YYYY-MM для input type="month"
      const year = currentMonth.getFullYear();
      const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
      setDateInputValue(`${year}-${month}`);
    }
  };

  const handleFullDateInputChange = (e) => {
    let value = e.target.value.replace(/[^\d.]/g, ''); // Только цифры и точки
    // Ограничиваем формат: дд.мм.гггг
    const parts = value.split('.');
    if (parts.length > 3) return;
    
    // Ограничиваем длину каждой части
    if (parts[0] && parts[0].length > 2) return;
    if (parts[1] && parts[1].length > 2) return;
    if (parts[2] && parts[2].length > 4) return;
    
    setFullDateInputValue(value);
  };

  const handleFullDateInputSubmit = (e) => {
    e.preventDefault();
    if (fullDateInputValue) {
      // Парсим формат дд.мм.гггг
      const parts = fullDateInputValue.split('.');
      if (parts.length === 3) {
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1; // Месяцы в JS начинаются с 0
        const year = parseInt(parts[2]);
        
        if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 2020 && year <= 2100) {
          const newDate = new Date(year, month, day);
          if (!isNaN(newDate.getTime())) {
            setCurrentMonth(newDate);
            // Если дата выбрана, автоматически выбираем её
            const dateStr = newDate.toISOString().split('T')[0];
            if (onDateSelect) {
              onDateSelect(dateStr);
            }
            setShowFullDateInput(false);
            setFullDateInputValue('');
          }
        }
      }
    }
  };

  const handleFullDateClick = () => {
    setShowFullDateInput(!showFullDateInput);
    if (!showFullDateInput) {
      // Устанавливаем текущую дату в формате дд.мм.гггг
      const day = String(currentMonth.getDate()).padStart(2, '0');
      const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
      const year = currentMonth.getFullYear();
      setFullDateInputValue(`${day}.${month}.${year}`);
    }
  };


  const formatFullDate = (date) => {
    let dateObj;
    // Парсим дату из строки "YYYY-MM-DD" в локальное время
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      // Создаем дату в ЛОКАЛЬНОМ времени
      dateObj = new Date(year, month - 1, day, 12, 0, 0, 0);
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      dateObj = new Date(date);
    }
    
    const localeCode = language === 'en' ? 'en-US' : 'ru-RU';
    return dateObj.toLocaleDateString(localeCode, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatMonthYear = (date) => {
    const localeCode = language === 'en' ? 'en-US' : 'ru-RU';
    return date.toLocaleDateString(localeCode, {
      month: 'long',
      year: 'numeric'
    });
  };

  // Получаем все дни месяца для отображения сетки
  const getMonthDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Добавляем пустые ячейки для дней предыдущего месяца
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Добавляем дни текущего месяца
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      // Форматируем дату в строку "YYYY-MM-DD" используя локальные компоненты
      // чтобы избежать проблем с timezone (toISOString() использует UTC)
      const dateYear = date.getFullYear();
      const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
      const dateDay = String(date.getDate()).padStart(2, '0');
      const dateStr = `${dateYear}-${dateMonth}-${dateDay}`;
      
      // Ищем доступность для этого дня
      // item.date может быть строкой "YYYY-MM-DD", ISO строкой "YYYY-MM-DDTHH:mm:ss.sssZ" или Date объектом
      const availableDate = availableDates.find(item => {
        if (!item || !item.date) return false;
        
        let itemDateStr;
        if (typeof item.date === 'string') {
          // Если это строка, проверяем формат
          if (/^\d{4}-\d{2}-\d{2}$/.test(item.date)) {
            // Строка "YYYY-MM-DD" - используем напрямую
            itemDateStr = item.date;
          } else if (/^\d{4}-\d{2}-\d{2}T/.test(item.date)) {
            // ISO строка "YYYY-MM-DDTHH:mm:ss.sssZ" - извлекаем дату из строки напрямую
            // Используем split('T')[0] чтобы получить "YYYY-MM-DD" без парсинга через Date
            // Это избегает проблем с timezone при конвертации
            itemDateStr = item.date.split('T')[0];
          } else {
            // Другой формат - пытаемся распарсить в локальное время
            const parsed = new Date(item.date);
            // Используем локальные компоненты, так как календарь тоже использует локальное время
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const day = String(parsed.getDate()).padStart(2, '0');
            itemDateStr = `${year}-${month}-${day}`;
          }
        } else if (item.date instanceof Date) {
          // Если это Date объект, конвертируем в строку
          // Используем локальные компоненты даты, чтобы избежать проблем с timezone
          const year = item.date.getFullYear();
          const month = String(item.date.getMonth() + 1).padStart(2, '0');
          const day = String(item.date.getDate()).padStart(2, '0');
          itemDateStr = `${year}-${month}-${day}`;
        } else {
          // Fallback для других форматов - парсим в локальное время
          const parsed = new Date(item.date);
          const year = parsed.getFullYear();
          const month = String(parsed.getMonth() + 1).padStart(2, '0');
          const day = String(parsed.getDate()).padStart(2, '0');
          itemDateStr = `${year}-${month}-${day}`;
        }
        
        // Сравниваем строки дат
        return itemDateStr === dateStr;
      });
      
      days.push({
        date,
        dateStr,
        day,
        available: availableDate,
        isPast: date < new Date().setHours(0, 0, 0, 0),
        isToday: dateStr === new Date().toISOString().split('T')[0]
      });
    }
    
    return days;
  };

  if (showTimeSlots && selectedDate) {
    return (
      <div className="time-slots-container">
        <div className="selected-date-info">
          <FaCalendarAlt />
          <span>{formatFullDate(selectedDate)}</span>
        </div>
        {loadingSlots ? (
          <div className="loading-slots">
            <div className="spinner"></div>
            <span>{t('booking.loadingTimeSlots')}</span>
          </div>
        ) : timeSlots.length === 0 ? (
          <div className="no-slots">
            <p>{t('booking.noTimeSlots')}</p>
            <p className="hint">{t('booking.noTimeSlotsHint')}</p>
          </div>
        ) : (
          <div className="time-slots-grid">
            {timeSlots.map((time) => {
              if (minTime) {
                const [minHours, minMinutes] = minTime.split(':').map(Number);
                const [timeHours, timeMinutes] = time.split(':').map(Number);
                const minTotal = minHours * 60 + minMinutes;
                const timeTotal = timeHours * 60 + timeMinutes;
                if (timeTotal <= minTotal) {
                  return null;
                }
              }
              
              return (
                <motion.button
                  key={time}
                  className={`time-slot ${selectedTime === time ? 'selected' : ''}`}
                  onClick={() => onTimeSelect && onTimeSelect(time)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaClock />
                  <span>{time}</span>
                  {selectedTime === time && <FaCheck className="check-icon" />}
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="calendar-loading">
        <div className="spinner"></div>
        <span>{t('booking.loadingCalendar')}</span>
      </div>
    );
  }

  if (availableDates.length === 0) {
    return (
      <div className="calendar-empty">
        <FaCalendarAlt />
        <p>{t('booking.noAvailableDates')}</p>
        <p className="hint">{t('booking.noAvailableDatesHint')}</p>
      </div>
    );
  }

  const monthDays = getMonthDays();
  const weekDays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

  return (
    <div className="booking-calendar-month">
      {/* Навигация месяца */}
      <div className="calendar-header">
        <button 
          className="calendar-nav-btn"
          onClick={handlePrevMonth}
          aria-label="Предыдущий месяц"
        >
          <FaChevronLeft />
        </button>
        
        <div className="calendar-month-title">
          {showDateInput ? (
            <form onSubmit={handleDateInputSubmit} className="date-input-form">
              <input
                type="month"
                value={dateInputValue}
                onChange={handleDateInputChange}
                className="date-input"
                autoFocus
                onBlur={() => {
                  setTimeout(() => {
                    if (dateInputValue) {
                      handleDateInputSubmit({ preventDefault: () => {} });
                    } else {
                      setShowDateInput(false);
                    }
                  }, 200);
                }}
              />
              <button type="submit" className="date-input-submit">✓</button>
              <button 
                type="button" 
                className="date-input-cancel"
                onClick={() => {
                  setShowDateInput(false);
                  setDateInputValue('');
                }}
              >
                ✕
              </button>
            </form>
          ) : (
            <>
              {showFullDateInput ? (
                <form onSubmit={handleFullDateInputSubmit} className="full-date-input-form">
                  <input
                    type="text"
                    value={fullDateInputValue}
                    onChange={handleFullDateInputChange}
                    className="full-date-input"
                    placeholder="дд.мм.гггг"
                    autoFocus
                    onBlur={() => {
                      setTimeout(() => {
                        if (fullDateInputValue) {
                          handleFullDateInputSubmit({ preventDefault: () => {} });
                        } else {
                          setShowFullDateInput(false);
                        }
                      }, 200);
                    }}
                  />
                  <button type="submit" className="full-date-input-submit">✓</button>
                  <button 
                    type="button" 
                    className="full-date-input-cancel"
                    onClick={() => {
                      setShowFullDateInput(false);
                      setFullDateInputValue('');
                    }}
                  >
                    ✕
                  </button>
                </form>
              ) : (
                <span 
                  onClick={handleFullDateClick} 
                  className="full-date-clickable"
                >
                  {String(currentMonth.getDate()).padStart(2, '0')}
                </span>
              )}
              <h3 onClick={handleMonthTitleClick} className="month-title-clickable">
                {formatMonthYear(currentMonth)}
              </h3>
            </>
          )}
        </div>
        
        <button 
          className="calendar-nav-btn"
          onClick={handleNextMonth}
          aria-label="Следующий месяц"
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Дни недели */}
      <div className="calendar-weekdays">
        {weekDays.map((day, index) => (
          <div key={index} className="weekday-header">
            {day}
          </div>
        ))}
      </div>

      {/* Календарная сетка */}
      <div className="calendar-grid">
        {monthDays.map((dayData, index) => {
          if (!dayData) {
            return <div key={index} className="calendar-day empty"></div>;
          }

          const { dateStr, day, available, isPast, isToday } = dayData;
          const isSelected = selectedDate && (
            typeof selectedDate === 'string' 
              ? selectedDate === dateStr
              : (() => {
                  // Если selectedDate - Date объект, конвертируем в строку "YYYY-MM-DD" используя локальные компоненты
                  const year = selectedDate.getFullYear();
                  const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
                  const day = String(selectedDate.getDate()).padStart(2, '0');
                  return `${year}-${month}-${day}` === dateStr;
                })()
          );
          // Проверяем доступность: дата доступна если есть available объект и есть слоты
          const isDisabled = !available || isPast || (available && available.availableSlots === 0);

          return (
            <motion.button
              key={dateStr}
              className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => !isDisabled && onDateSelect && onDateSelect(dateStr)}
              whileHover={!isDisabled ? { scale: 1.05, y: -2 } : {}}
              whileTap={!isDisabled ? { scale: 0.95 } : {}}
              disabled={isDisabled}
            >
              <span className="day-number">{day}</span>
              {isSelected && <FaCheck className="check-icon" />}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default BookingCalendar;
